import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import { Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import styles from './DailyTimeReport.module.scss';
import DTRService from '../../services/DTRService';
import { useAppSelector, useAppDispatch } from '../../state/hooks';
import type { RootState } from '../../state/store';
import { setPageTitle } from '../../state/slices/pageTitle';
import MainSection from '../common/MainSection';
import LoadingProgress from '../common/LoadingProgress/LoadingProgress.lazy';
import {
  drawerWidth,
  headerHeight,
  mainPadding,
  sideBarClosedWidth,
  sideBarMarginWidth,
  sideBarOpenedWidth
} from '../../theme/styles';
import DailyTimeReportTable from './DailyTimeReportTable/DailyTimeReportTable.lazy';
import DailyTimeReportButtons from './DailyTimeReportButtons/DailyTimeReportButtons.lazy';
import { textAreaHeight } from './DailyTimeReportTable/DailyTimeReportTable';
import AddMembersDTR from '../modals/AddMembersDTR/AddMembersDTR.lazy';
import DTRSuccess from '../modals/DTRSuccess/DTRSuccess.lazy';
import OrderMembersDTR from '../modals/OrderMembersDTR/OrderMembersDTR.lazy';
import DailyDTRNoReview from '../modals/DailyDTRNoReview/DailyDTRNoReview.lazy';
import DailyDTRReport from '../modals/DailyDTRReport/DailyDTRReport.lazy';
import WeeklyDTRReport from '../modals/WeeklyDTRReport/WeeklyDTRReport.lazy';

export enum DailyTimeReportPage {
  Title = 'Daily Time Report'
}

export interface DTRButtonsForm {
  dtrSettings: string
  department: string
}

export interface DailyTimeReportProps {
  pageTitle: DailyTimeReportPage
}

const DailyTimeReport: FC<DailyTimeReportProps> = ({ pageTitle }: DailyTimeReportProps) => {
  const dispatch = useAppDispatch();
  const fetchingData: boolean = useAppSelector((state: RootState) => state.dailyTimeReport.fetchingData);
  const buildingReport: boolean = useAppSelector((state: RootState) => state.dailyTimeReport.buildingReport);
  const isSideBarOpen: boolean = useAppSelector((state: RootState) => state.sideBar.isSideBarOpen);
  const userDepartmentId: string = useAppSelector((state: RootState) => state.dailyTimeReport.userDepartmentId);

  const [tableWidth, setTableWidth] = useState<number>(0);
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [showTravelTime, setShowTravelTime] = useState<boolean>(false);
  const [showSecondMeal, setShowSecondMeal] = useState<boolean>(false);

  // default values for form
  const defaultValues: DTRButtonsForm = {
    dtrSettings: '',
    department: ''
  };

  const { control, setValue, getValues } = useForm<DTRButtonsForm>({
    defaultValues
  });

  useEffect(() => {
    if (userDepartmentId && getValues('department') === '') {
      setValue('department', userDepartmentId);
    }
  }, [userDepartmentId]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    }
  })

  useEffect(() => {
    handleResize();
  }, [isSideBarOpen]);

  useEffect(() => {
    dispatch(setPageTitle(pageTitle));
    void DTRService.loadReportData();
  }, [pageTitle]);

  const handleResize = (): void => {
    const windowHeight: number = window.innerHeight;
    const windowWidth: number = window.innerWidth;
    let width = windowWidth - sideBarMarginWidth - mainPadding - 40;
    const height = windowHeight - mainPadding - headerHeight - textAreaHeight - 60;
    if (isSideBarOpen) {
      width = width - sideBarOpenedWidth;
    } else {
      width = width - sideBarClosedWidth;
    }
    setTableHeight(height);
    setTableWidth(width);
  }

  return (
    <MainSection open={true} drawerwidth={drawerWidth} sx={{ height: '100vh' }}>
      {(fetchingData)
        ? <LoadingProgress isLoading={fetchingData} />
        : <Grid container className={styles.dailyTimeReport} spacing={2}>
            {buildingReport && <LoadingProgress isLoading={buildingReport} />}
            <DailyTimeReportButtons
              showTravelTime={showTravelTime}
              setShowTravelTime={setShowTravelTime}
              showSecondMeal={showSecondMeal}
              setShowSecondMeal={setShowSecondMeal}
              tableWidth={tableWidth}
              control={control}
              setValue={setValue}
              getValues={getValues}
            />
            <DailyTimeReportTable
              tableWidth={tableWidth}
              tableHeight={tableHeight}
              showTravelTime={showTravelTime}
              showSecondMeal={showSecondMeal}
              getValues={getValues}
            />
          </Grid>
      }
      <AddMembersDTR />
      <DTRSuccess />
      <OrderMembersDTR />
      <DailyDTRNoReview />
      <DailyDTRReport />
      <WeeklyDTRReport />
    </MainSection>
  )
}

export default DailyTimeReport;
